import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import { useRecoilValue } from "recoil";
import { fetchAPIResponseSelector } from "../../store/user.atom";
import { IngredientDTO } from "../../dtos/ingredient.dto";
import {toast} from "react-toastify";

export interface Ingredient {
    id: string;
    name: string;
}

export interface IngredientOption {
    value: string;
    label: string;
}

export interface SelectedIngredient {
    ingredient: IngredientOption;
    quantity: number;
    minQuantity: number;
    maxQuantity: number;
}

export interface IngredientSelectorProp {
    type: string;
    ingredients: SelectedIngredient[];
    setIngredients: React.Dispatch<React.SetStateAction<SelectedIngredient[]>>;
}

const IngredientSelector: React.FC<IngredientSelectorProp> = ({ type, ingredients, setIngredients }) => {
    const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);

    const fetchIngredients = async (inputValue: string): Promise<IngredientOption[]> => {
        try {
            const ingredients = await fetchAPIResponse<IngredientDTO[]>(`/ingredients?search=${inputValue}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
            });

            return ingredients.data.map((ingredient: Ingredient) => ({
                value: ingredient.id,
                label: ingredient.name
            }));
        } catch (error) {
            console.error('Error fetching ingredients:', error);
            return [];
        }
    };

    const handleIngredientSelect = (index: number, selectedOption: IngredientOption | null) => {
        if (selectedOption) {
            const updatedIngredients = [...ingredients];
            updatedIngredients[index].ingredient = selectedOption;
            setIngredients(updatedIngredients);
        }
    };

    const handleIngredientQuantityChange = (index: number, field: 'quantity' | 'minQuantity' | 'maxQuantity', value: number) => {
        const updatedIngredients = [...ingredients];
        if (field === 'quantity') {
            if (value >= updatedIngredients[index].minQuantity && value <= updatedIngredients[index].maxQuantity) {
                updatedIngredients[index][field] = value;
            } else {
                toast.error('Quantity must be between min and max quantities');
                return;
            }
        } else if (field === 'minQuantity') {
            if (value <= updatedIngredients[index].maxQuantity) {
                updatedIngredients[index][field] = value;
                if (updatedIngredients[index].quantity < value) {
                    updatedIngredients[index].quantity = value;
                }
            } else {
                toast.error('Min quantity cannot be greater than max quantity');
                return;
            }
        } else if (field === 'maxQuantity') {
            if (value >= updatedIngredients[index].minQuantity) {
                updatedIngredients[index][field] = value;
                if (updatedIngredients[index].quantity > value) {
                    updatedIngredients[index].quantity = value;
                }
            } else {
                toast.error('Max quantity cannot be less than min quantity');
                return;
            }
        }
        setIngredients(updatedIngredients);
    };

    const handleAddMore = () => {
        setIngredients(prevState => [...prevState, { ingredient: { value: '', label: '' }, quantity: 0, minQuantity: 0, maxQuantity: 0 }]);
    };

    const handleDelete = (index: number) => {
        setIngredients(prevState => prevState.filter((_, i) => i !== index));
    };

    return (
        <div className={"my-6"}>
            <label className='zbh-label'>{type}</label>
            <div className='mt-2 rounded-xl border border-hair p-3 max-h-56 overflow-y-auto bg-canvas/30'>
                <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="flex-1" />
                    <span className="zbh-th w-16 text-center">Qty</span>
                    <span className="zbh-th w-16 text-center">Min</span>
                    <span className="zbh-th w-16 text-center">Max</span>
                    <span className="w-9" />
                </div>
                {ingredients.length === 0 && (
                    <p className="px-1 py-2 text-xs text-categoriesTextColor">No {type.toLowerCase()} yet — add one below.</p>
                )}
                {ingredients.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2">
                        <AsyncSelect
                            cacheOptions
                            loadOptions={fetchIngredients}
                            defaultOptions
                            value={item.ingredient}
                            onChange={(selectedOption) => handleIngredientSelect(index, selectedOption)}
                            className="flex-1"
                            placeholder="Select ingredient"
                            styles={{
                                menuPortal: (base) => ({...base, zIndex: 9999}),
                                container: (base) => ({...base, flex: 1}),
                                control: (base, state) => ({
                                    ...base,
                                    borderRadius: 12,
                                    borderColor: state.isFocused ? '#E5A823' : '#ECE7DE',
                                    boxShadow: state.isFocused ? '0 0 0 4px rgba(229,168,35,0.15)' : 'none',
                                    minHeight: 40,
                                    '&:hover': { borderColor: '#E5A823' },
                                }),
                            }}
                            menuPortalTarget={document.body}
                        />
                        <input
                            type="number" placeholder="Qty" value={item.quantity}
                            onChange={(e) => handleIngredientQuantityChange(index, 'quantity', parseFloat(e.target.value))}
                            className="zbh-input w-16 px-2 text-center"
                        />
                        <input
                            type="number" placeholder="Min" value={item.minQuantity}
                            onChange={(e) => handleIngredientQuantityChange(index, 'minQuantity', parseFloat(e.target.value))}
                            className="zbh-input w-16 px-2 text-center"
                        />
                        <input
                            type="number" placeholder="Max" value={item.maxQuantity}
                            onChange={(e) => handleIngredientQuantityChange(index, 'maxQuantity', parseFloat(e.target.value))}
                            className="zbh-input w-16 px-2 text-center"
                        />
                        <button
                            onClick={() => handleDelete(index)}
                            aria-label="Remove"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red/10 text-red transition hover:bg-red/15"
                        >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                              <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={handleAddMore} className="zbh-btn-ghost mt-3 px-4 py-1.5 text-xs">
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add {type.toLowerCase()}
            </button>
        </div>
    );
};

export default IngredientSelector;
